import { useCallback, useEffect, useMemo, useState } from "react";
import PercentIcon from "@mui/icons-material/Percent";
import SyncIcon from "@mui/icons-material/Sync";
import TableCard from "../../components/TableCard";
import SnackBar from "../../components/SnackBar";
import {
  buscarTributacaoSelicAcumulada,
  executarAtualizacaoSelic,
} from "../../services/api-planejamento-tributario.service";
import { Token } from "../../utils/localStorage.util";
import styles from "./styles.module.css";

const columns = ["Referência", "Taxa %"];

const formatReferencia = (referencia) => {
  if (!referencia) return "";
  const texto = String(referencia).trim();
  const match = texto.match(/^(\d{4})-(\d{2})$/);
  if (match) return `${match[2]}/${match[1]}`;
  return texto;
};

const formatTaxa = (taxa) => {
  const valor = Number(taxa);
  if (Number.isNaN(valor)) return "-";
  if (valor === -1) return "Aguardando";
  return `${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const normalizarListaSelic = (response) => {
  const lista =
    response?.selic ??
    response?.dados ??
    response?.taxaSelic ??
    response?.data ??
    (Array.isArray(response) ? response : []);

  return (lista || [])
    .map((item) => ({
      referencia: item.referencia ?? item.competencia ?? item.periodo ?? "",
      taxa: item.taxa ?? item.valor ?? item.taxaAcumulada ?? item.acumulada,
    }))
    .filter((item) => item.referencia);
};

const montarMensagemAtualizacao = (response) => {
  const pendentes = response?.pendentes ?? [];
  const ultimaRef = formatReferencia(response?.ultimaReferencia);
  const ultimaTaxa = response?.ultimaTaxa;

  if (response?.selicEstaAtualizada) {
    const taxaTexto =
      ultimaTaxa !== null && ultimaTaxa !== undefined && Number(ultimaTaxa) !== -1
        ? formatTaxa(ultimaTaxa)
        : "";
    const refTexto = ultimaRef ? ` Última referência: ${ultimaRef}` : "";
    const taxaSuffix = taxaTexto ? ` (${taxaTexto}).` : ".";
    return (
      response?.mensagem ||
      `SELIC atualizada com sucesso. Tabela em dia.${refTexto}${taxaSuffix}`
    );
  }

  if (pendentes.length > 0) {
    const refs = pendentes
      .map((item) => formatReferencia(item.referencia))
      .join(", ");
    return (
      response?.mensagem ||
      `Rotina executada. Pendente(s): ${refs}. Clique em Atualizar Selic novamente quando o valor oficial estiver disponível.`
    );
  }

  return (
    response?.mensagem ||
    "Rotina executada, mas a tabela ainda não está totalmente atualizada."
  );
};

const AtualizarSelic = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const handleSnackBar = useCallback((obj) => {
    setObjSnackBar(obj);
  }, []);

  const organizarDados = useCallback((lista) => {
    return (lista || [])
      .slice()
      .sort((a, b) => String(b.referencia).localeCompare(String(a.referencia)))
      .map((item) => ({
        referencia: formatReferencia(item.referencia),
        taxa: formatTaxa(item.taxa),
      }));
  }, []);

  const carregarSelic = useCallback(async () => {
    const token = Token.getToken();
    if (!token) {
      setData([]);
      setLoading(false);
      handleSnackBar({
        type: "error",
        msg: "Sessão expirada. Faça login novamente.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await buscarTributacaoSelicAcumulada();
      setData(organizarDados(normalizarListaSelic(response)));
    } catch (error) {
      setData([]);
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          "Erro ao carregar a tabela SELIC.",
      });
    } finally {
      setLoading(false);
    }
  }, [handleSnackBar, organizarDados]);

  useEffect(() => {
    carregarSelic();
  }, [carregarSelic]);

  const handleAtualizarSelic = useCallback(async () => {
    if (atualizando) return;
    setAtualizando(true);
    try {
      const response = await executarAtualizacaoSelic();
      const msg = montarMensagemAtualizacao(response);
      handleSnackBar({
        type: response?.selicEstaAtualizada ? "success" : "warning",
        msg,
      });
      await carregarSelic();
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          error.message ||
          "Erro ao atualizar SELIC. Tente novamente.",
      });
    } finally {
      setAtualizando(false);
    }
  }, [carregarSelic, handleSnackBar]);

  const hoverBtnHeader = useMemo(
    () => [
      {
        icon: <SyncIcon sx={{ color: "inherit", fontSize: "1.1rem" }} />,
        label: atualizando ? "Atualizando..." : "Atualizar Selic",
        onClick: handleAtualizarSelic,
        disabled: atualizando || loading,
        alwaysOpen: true,
        variant: "primary",
      },
    ],
    [atualizando, handleAtualizarSelic, loading]
  );

  return (
    <>
      <TableCard
        iconHeader={<PercentIcon fontSize="1rem" />}
        titleHeader="Tabela Selic"
        data={data}
        setData={setData}
        columns={columns}
        loading={loading || atualizando}
        textHeaderCard="referencia"
        hoverBtnHeader={hoverBtnHeader}
        tableClassName={styles.tableSelic}
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />
    </>
  );
};

export default AtualizarSelic;
